import { getBlurhashFromBlob } from "@/shared/lib/get-blurhash-client";
import { compressToWebP } from "@/shared/lib/image-compress";
import { uploadApi } from "@/shared/lib/upload-api";
import { uploadFileToPresignedUrl } from "@/shared/lib/upload-to-presigned";
import { Loader2, Plus, Upload } from "lucide-react";
import { useCallback, useId, useState } from "react";

function sanitizeFilename(name: string): string {
  return name.replace(/[^a-zA-Z0-9._-]/g, "_").slice(0, 100) || "image";
}

export type FileUploadItemStatus = "pending" | "uploading" | "done" | "error";

export type PresignedImageUploadTriggerProps = {
  /** Prefix ของ key ใน bucket เช่น "uploads/products" */
  keyPrefix?: string;
  /** เมื่ออัปโหลดสำเร็จ 1 ไฟล์ (key, blurhash จาก client) */
  onSuccess?: (key: string, blurhash?: string | null) => void;
  /** เมื่ออัปโหลดหลายไฟล์สำเร็จ (keys, blurhashes จาก client) */
  onSuccessMultiple?: (keys: string[], blurhashes: string[]) => void;
  /** เรียกเมื่อผู้ใช้เลือกไฟล์ (ก่อนอัปโหลด) — ใช้แสดง preview */
  onFilesSelected?: (files: File[]) => void;
  /** สถานะของแต่ละไฟล์: index, status, key (เมื่อ done) */
  onFileProgress?: (
    index: number,
    status: FileUploadItemStatus,
    key?: string,
  ) => void;
  onError?: (error: Error) => void;
  /** รูปแบบ: ปุ่มในแถบเครื่องมือ หรือช่องใน grid */
  variant?: "button" | "tile";
  /** ข้อความปุ่ม (ใช้เมื่อ variant="button") */
  label?: string;
  /** ให้เลือกหลายรูปได้ */
  multiple?: boolean;
  disabled?: boolean;
  /** กำลังบันทึกลง DB (Phase 2) — แสดง loading และ disable */
  isSaving?: boolean;
  /** แจ้งเมื่อกำลังอัปโหลดไปยัง storage (ไม่รวม isSaving) */
  onBusyChange?: (busy: boolean) => void;
  className?: string;
};

export function PresignedImageUploadTrigger({
  keyPrefix = "uploads/products",
  onSuccess,
  onSuccessMultiple,
  onFilesSelected,
  onFileProgress,
  onError,
  variant = "button",
  label = "ອັບໂຫຼດຮູບ",
  multiple = false,
  disabled = false,
  isSaving = false,
  onBusyChange,
  className = "",
}: PresignedImageUploadTriggerProps) {
  const inputId = useId();
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState<{
    current: number;
    total: number;
  } | null>(null);
  const [error, setError] = useState<string | null>(null);

  const uploadOneFile = useCallback(
    async (file: File, index: number): Promise<{ key: string; blurhash: string | null }> => {
      const compressedBlob = await compressToWebP(file);
      const blurhash = await getBlurhashFromBlob(compressedBlob);
      const baseName = file.name.includes(".")
        ? file.name.slice(0, file.name.lastIndexOf("."))
        : file.name;
      const safeBase = sanitizeFilename(baseName);
      const key = `${keyPrefix}/${Date.now()}-${index}-${safeBase}.webp`;
      const contentType = "image/webp";

      const { uploadUrl } = await uploadApi.getPresignUrl({
        key,
        contentType,
      });
      await uploadFileToPresignedUrl(uploadUrl, compressedBlob, { contentType });
      return { key, blurhash };
    },
    [keyPrefix],
  );

  const handleChange = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      const fileList = e.target.files;
      const files = fileList ? Array.from(fileList) : [];
      e.target.value = "";

      if (!files.length) return;
      setError(null);
      setUploading(true);
      onBusyChange?.(true);
      const total = files.length;
      setUploadProgress(total > 1 ? { current: 0, total } : null);

      onFilesSelected?.(files);

      try {
        const keys: string[] = [];
        const blurhashes: string[] = [];
        await new Promise<void>((resolve) => setTimeout(resolve, 0));

        for (let i = 0; i < files.length; i++) {
          if (total > 1) setUploadProgress({ current: i, total });
          const file = files[i];
          if (!file) continue;

          onFileProgress?.(i, "uploading");
          try {
            const { key, blurhash } = await uploadOneFile(file, i);
            keys.push(key);
            blurhashes.push(blurhash ?? "");
            onFileProgress?.(i, "done", key);
          } catch (fileErr) {
            onFileProgress?.(i, "error");
            throw fileErr;
          }
        }

        const firstKey = keys[0];
        const firstBlurhash = blurhashes[0] ?? "";
        if (keys.length === 1 && firstKey !== undefined) {
          onSuccess?.(firstKey, firstBlurhash || undefined);
        } else if (keys.length > 1 && onSuccessMultiple) {
          onSuccessMultiple(keys, blurhashes);
        }
      } catch (e) {
        const err = e instanceof Error ? e : new Error(String(e));
        setError(err.message);
        onError?.(err);
      } finally {
        setUploading(false);
        setUploadProgress(null);
        onBusyChange?.(false);
      }
    },
    [
      uploadOneFile,
      onSuccess,
      onSuccessMultiple,
      onFilesSelected,
      onFileProgress,
      onError,
      onBusyChange,
    ],
  );

  const triggerClick = useCallback(() => {
    if (disabled || uploading || isSaving) return;
    document.getElementById(inputId)?.click();
  }, [inputId, disabled, uploading, isSaving]);

  const isTile = variant === "tile";
  const isLoading = uploading || isSaving;

  const loadingLabel = isSaving
    ? "ກຳລັງບັນທຶກ..."
    : uploadProgress && uploadProgress.total > 1
      ? `ກຳລັງອັບໂຫຼດ ${uploadProgress.current + 1}/${uploadProgress.total}`
      : "ກຳລັງອັບໂຫຼດ...";

  return (
    <div className={className}>
      <input
        type="file"
        accept="image/*"
        multiple={multiple}
        onChange={handleChange}
        disabled={disabled || isLoading}
        className="hidden"
        id={inputId}
        aria-label={label}
      />
      {isTile ? (
        <button
          type="button"
          onClick={triggerClick}
          disabled={disabled || isLoading}
          className="flex aspect-square w-full cursor-pointer flex-col items-center justify-center gap-2 rounded-lg border-2 border-muted-foreground/25 border-dashed bg-muted/10 px-6 py-2 transition-colors hover:border-primary/50 hover:bg-muted/20 focus-visible:outline focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
        >
          {isLoading ? (
            <Loader2 className="size-8 animate-spin text-muted-foreground" />
          ) : (
            <Plus className="size-8 text-muted-foreground" />
          )}
          <span className="text-muted-foreground text-xs">
            {isLoading ? loadingLabel : "ເພີ່ມຮູບ"}
          </span>
        </button>
      ) : (
        <button
          type="button"
          onClick={triggerClick}
          disabled={disabled || isLoading}
          className="inline-flex cursor-pointer items-center gap-2 rounded-md border border-input bg-background px-3 py-2 font-medium text-sm transition-colors hover:bg-accent hover:text-accent-foreground focus-visible:outline focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
        >
          {isLoading ? (
            <Loader2 className="size-4 shrink-0 animate-spin" />
          ) : (
            <Upload className="size-4 shrink-0" />
          )}
          <span>{isLoading ? loadingLabel : label}</span>
        </button>
      )}
      {error && (
        <p className="mt-1.5 text-destructive text-sm" role="alert">
          {error}
        </p>
      )}
    </div>
  );
}
